import { debounce } from 'debounce';
import React, {
  MutableRefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Playlist from '../../../models/playlist/playlist';
import PlaylistItem from '../../../models/playlistItem/playlistItem';
import Track from '../../../models/track/track';
import { ActivePlaylistContext } from '../../../services/activePlaylist/activePlaylist';
import { ColumnScope, TrackProperty } from '../../../services/columnSettings/playlistColumns';
import { LibraryContext } from '../../../services/library/libraryService';
import { focusIsOnControl, isTyping } from '../../../services/shortcuts/keys';
import { libraryTracksOf, trackToPlay } from '../../../services/shortcuts/selectionActions';
import useShortcut from '../../../services/shortcuts/useShortcut';
import {
  createFilterBy,
  createSortByProp,
  createSortByPropDescending,
  ISorter,
  sortByIndex,
  sortByIndexDescending,
} from '../../playlist/playlistSorters';
import useColumnSettings from './useColumnSettings';

interface Params<T> {
  lastSelectedItem: MutableRefObject<T | undefined>;
  selectedItemsRef: MutableRefObject<Set<T>>;
  setSelectedItems: React.Dispatch<SetStateAction<MutableRefObject<Set<T>>>>;
  playlist: Playlist;
  items: T[];
  moveableProp: boolean | undefined;
  columnScope: ColumnScope;
  /** Removes rows from this playlist. Absent where that is not offered, and then neither is the Delete key. */
  removeItems?(...items: T[]): void;
}
export default function useMoreSharedPlaylistMethods<T extends PlaylistItem | Track>({
  playlist,
  items,
  lastSelectedItem,
  selectedItemsRef,
  setSelectedItems,
  moveableProp,
  columnScope,
  removeItems,
}: Params<T>) {
  const activePlaylist = useContext(ActivePlaylistContext);
  const { columnSettings } = useColumnSettings(columnScope);

  const libCtx = useContext(LibraryContext);

  if (columnSettings) {
    var defaultColumn = (Object.keys(columnSettings) as TrackProperty[]).find(
      (col) => columnSettings[col]?.default,
    );
  }

  const updateFilter = useCallback(
    debounce<(val: string) => void>(function updateFilter(val: string) {
      setFilter(val);
    }, 150),
    [],
  );

  const sortByProp = useMemo(() => createSortByProp(libCtx), [libCtx]);
  const sortByPropDescending = useMemo(() => createSortByPropDescending(libCtx), [libCtx]);
  const [sortBy, setStateSortBy] = useState<ISorter<PlaylistItem | Track>>(
    defaultColumn ? sortByProp(defaultColumn) : sortByIndex,
  );
  const setSortBy = useCallback(
    function setSortBy(column: TrackProperty | 'index', descending?: true) {
      if (column === 'index') {
        if (descending) {
          setStateSortBy(sortByIndexDescending);
        } else {
          setStateSortBy(sortByIndex);
        }
      } else {
        if (descending) {
          setStateSortBy(sortByPropDescending(column));
        } else {
          setStateSortBy(sortByProp(column));
        }
      }

      // colCtx.setColumnDefault(column, columnScope);
    },
    [sortByProp, sortByPropDescending],
  );

  const [filter, setFilter] = useState<string>('');
  /**
   * Empties the filter now, and tells the filter box - whose text is its
   * own state, see PlaylistControls - to empty too. For the keys that clear
   * a filter the user did not just type: Escape, and ctrl+L finding a track
   * the filter was hiding.
   */
  const [filterResetKey, setFilterResetKey] = useState(0);
  const resetFilter = useCallback(
    function resetFilter() {
      updateFilter.clear();
      setFilter('');
      setFilterResetKey((key) => key + 1);
    },
    [updateFilter],
  );
  // No explicit <T> here: the exhaustive-deps rule this project runs mistakes
  // a type parameter referenced inside a hook callback for a missing runtime
  // dependency. The constraint's predicate is assignable to a T[] filter.
  const filterBy = useMemo(() => createFilterBy(libCtx), [libCtx]);

  const filteredAndSortedTracks = useMemo(
    () => items.filter(filterBy(filter)).sort(sortBy),
    [items, filter, filterBy, sortBy],
  );

  // The parameter type lives in the type argument, outside the callback, for
  // the same exhaustive-deps reason as filterBy above.
  const handlePlayTrack = useCallback<(plItem: T) => void>(
    function (plItem) {
      const doPlay = true;

      if ((plItem as PlaylistItem).trackType) {
        activePlaylist.setActivePlaylist(
          filteredAndSortedTracks as PlaylistItem[],
          filteredAndSortedTracks.indexOf(plItem),
          doPlay,
          // Where ctrl+L brings the listener back to. For a library playlist
          // this is its id; a streaming service's track list has an id of its
          // own that no library playlist carries, and playlistToShow treats
          // that as "not from a playlist", which is what it is.
          playlist.id,
        );
      } else {
        const filtered = (filteredAndSortedTracks as Track[]).map(PlaylistItem.from);

        // TrackPlaylists add all tracks to active playlist
        // regardless of filtering
        activePlaylist.setActivePlaylist(
          filtered,
          filtered.findIndex((x) => x.id === plItem.id),
          doPlay,
          playlist.id,
        );
      }
    },
    [activePlaylist, filteredAndSortedTracks, playlist.id],
  );

  const prevPlaylistRef = useRef<Playlist>(undefined);
  /**
   * This triggers on component re-render, f.x. on playlist change
   */
  useEffect(
    function resetOnChangePlaylist() {
      if (prevPlaylistRef.current && prevPlaylistRef.current.id !== playlist.id) {
        setFilter('');
        setSortBy('index');
        lastSelectedItem.current = undefined;

        selectedItemsRef.current.clear();
      }
    },
    [playlist, selectedItemsRef, lastSelectedItem, setSortBy],
  );
  /**
   * Helps in cases of selected tracks deleted from library.
   */
  useEffect(
    function handleChangedItems() {
      if (prevPlaylistRef.current && prevPlaylistRef.current.id === playlist.id) {
        console.debug('Handling changed item');

        let didChange = false;

        selectedItemsRef.current.forEach((item) => {
          if (
            !playlist.tracks.some((x) => x.id === item.id) &&
            selectedItemsRef.current.delete(item)
          ) {
            didChange = true;
          }
        });

        if (didChange) {
          setSelectedItems({
            current: selectedItemsRef.current,
          });
        }
      }
    },
    [playlist.tracks],
  );
  useEffect(() => {
    prevPlaylistRef.current = playlist;
  });

  /*
    The keyboard on a selection. Registered here rather than in the views
    because this is where the selection, the library and the play call all
    are; both playlist views get them by using this hook.
  */
  useShortcut({
    id: 'select-all',
    keys: { key: 'a', mod: true },
    group: 'Library',
    description: 'Select all tracks in view',
    run: () => {
      setSelectedItems({
        current: new Set(filteredAndSortedTracks),
      });
    },
  });

  useShortcut({
    id: 'play-selected',
    keys: { key: 'Enter' },
    group: 'Library',
    description: 'Play the selected track',
    run: () => {
      // A focused button or sidebar row has its own Enter.
      if (focusIsOnControl()) {
        return false;
      }

      const item = trackToPlay(
        lastSelectedItem.current,
        selectedItemsRef.current,
        filteredAndSortedTracks,
      );

      if (item === undefined) {
        return false;
      }

      handlePlayTrack(item);
    },
  });

  useShortcut({
    id: 'edit-info',
    keys: { key: 'i', mod: true },
    group: 'Library',
    description: 'Edit info for the selected tracks',
    whileTyping: true,
    run: () => {
      const tracks = libraryTracksOf(selectedItemsRef.current, libCtx.getLibraryTrack);

      if (tracks.length === 0) {
        return false;
      }

      libCtx.openTrackEditor(tracks);
    },
  });

  useShortcut(
    {
      id: 'remove-selected',
      keys: { key: 'Delete', aliases: ['Backspace'] },
      group: 'Library',
      description: 'Remove the selected tracks from this playlist',
      run: () => {
        if (!removeItems || selectedItemsRef.current.size === 0) {
          return false;
        }

        // The same as the menu's Delete Selected, confirmation included where
        // the playlist is the library itself.
        removeItems(...selectedItemsRef.current);
        selectedItemsRef.current.clear();
        setSelectedItems({ current: selectedItemsRef.current });
      },
    },
    removeItems !== undefined,
  );

  useShortcut({
    id: 'clear-selection',
    keys: { key: 'Escape' },
    group: 'Library',
    description: 'Clear the selection, or the filter box when you are in it',
    whileTyping: true,
    run: () => {
      const active = document.activeElement as HTMLElement | null;

      if (active && isTyping(active)) {
        if (active.id !== 'filter') {
          return false;
        }

        resetFilter();
        active.blur();

        return;
      }

      if (selectedItemsRef.current.size === 0) {
        return false;
      }

      selectedItemsRef.current.clear();
      setSelectedItems({ current: selectedItemsRef.current });
    },
  });

  const sortOnClickCreator = useCallback(
    function (propName: TrackProperty) {
      return function (ev: React.MouseEvent) {
        ev.stopPropagation();
        ev.preventDefault();

        if (sortBy.propName === propName) {
          return setSortBy(propName, true);
        } else {
          return setSortBy(propName);
        }
      };
    },
    [sortBy, setSortBy],
  );

  const moveable =
    moveableProp &&
    // It's weird to reorder items when the playlist is statically sorted
    (sortBy.propName === 'index' || sortBy.propName === 'index-descending');

  return {
    sortBy,
    sortByProp,
    setSortBy,
    sortOnClickCreator,
    sortByPropDescending,

    filter,
    updateFilter,
    resetFilter,
    filterResetKey,

    filteredAndSortedTracks,
    handlePlayTrack,

    moveable,
  };
}
